import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from '../UI/Button';
import Toast from '../UI/Toast';
import { guestAPI, ImportResponse, ValidationError, PreviewRow, PreviewImportResponse } from '../../services/guestAPI';
import { Download, AlertTriangle, CheckCircle, X, FileText, Users, HelpCircle, Phone, Sparkles, Lightbulb, ArrowRight, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAppSelector } from '../../store/hooks';
import { logError } from '../../utils/errorLogger';
import { delay } from '../../utils/delay';
import GuestImportPreview from './GuestImportPreview';
// Removed deprecated phone validation import

interface GuestImportModalProps {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

interface ImportOptions {
  skip_duplicates: boolean;
  send_invites: boolean;
  validate_only: boolean;
}

// Import flow steps
type ImportStep = 'upload' | 'preview' | 'importing';

export const GuestImportModal: React.FC<GuestImportModalProps> = ({
  eventId,
  isOpen,
  onClose,
  onImportComplete
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [options, setOptions] = useState<ImportOptions>({
    skip_duplicates: true,
    send_invites: false,
    validate_only: false,
  });
  const [showHelp, setShowHelp] = useState(false);
  
  // Preview state
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [previewData, setPreviewData] = useState<PreviewImportResponse['data'] | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Helper function to format validation errors
  const formatValidationErrors = (errors: ValidationError[]): string => {
    if (!errors || errors.length === 0) return 'No validation errors found.';
    
    const errorCount = errors.length;
    const uniqueErrors = new Map<string, number>();
    
    errors.forEach(error => {
      if (error && typeof error === 'object') {
        const errorType = String(error.error_type || 'unknown');
        const errorMessage = String(error.error_message || 'unknown error');
        const key = `${errorType}: ${errorMessage}`;
        uniqueErrors.set(key, (uniqueErrors.get(key) || 0) + 1);
      }
    });
    
    let errorSummary = `Validation failed! Found ${errorCount} error${errorCount > 1 ? 's' : ''}:\n\n`;
    
    uniqueErrors.forEach((count, error) => {
      errorSummary += `• ${error}`;
      if (count > 1) {
        errorSummary += ` (${count} occurrences)`;
      }
      errorSummary += '\n';
    });
    
    // Add first few row details
    const sampleErrors = errors.slice(0, 3);
    if (sampleErrors.length > 0) {
      errorSummary += '\nSample errors:\n';
      sampleErrors.forEach(error => {
        if (error && typeof error === 'object') {
          const rowNumber = String(error.row_number || 'unknown');
          const errorMessage = String(error.error_message || 'unknown error');
          errorSummary += `Row ${rowNumber}: ${errorMessage}\n`;
        }
      });
      if (errors.length > 3) {
        errorSummary += `... and ${errors.length - 3} more errors\n`;
      }
    }
    
    return errorSummary;
  };

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
    ];
    
    const allowedExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExtension)) {
      setError('Please select a valid Excel (.xlsx, .xls) or CSV file');
      return;
    }

    // Validate file size (5MB limit)
    if (selectedFile.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirmImport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const importOptions = {
        ...options,
        validate_only: false
      };

      const result = await guestAPI.importGuests(eventId, file!, importOptions);
      setImportResult(result);

      if (result.success) {
        const successful = result.data?.results?.successful || 0;
        const failed = result.data?.results?.failed || 0;
        const total = result.data?.results?.total_processed || 0;
        
        if (successful > 0) {
          setSuccess(`Import completed! ${successful} of ${total} guest(s) successfully added to event.${failed > 0 ? ` ${failed} row(s) failed.` : ''}`);
          onImportComplete();
          
          // Close modal after a brief delay
          await delay(2000);
          handleClose();
        } else {
          const errorDetails = result.data?.error_details || [];
          const errorSummary = result.data?.results?.error_summary || [];
          
          if (errorDetails.length > 0) {
            setImportResult({
              ...result,
              validation_errors: errorDetails
            });
            setError(`Import failed: No guests were added. ${errorSummary.length > 0 ? errorSummary.join(', ') : 'Check error details below.'}`);
          } else {
            setError(`Import failed: No guests were added to the event. Please check the file and try again.`);
          }
        }
      } else {
        const errorMessage = String(result.message || 'Import failed');
        const validationErrors = result.validation_errors || [];
        
        if (validationErrors.length > 0) {
          const errorSummary = formatValidationErrors(validationErrors);
          setError(String(errorSummary));
        } else {
          setError(`${errorMessage}. Please check the file and try again.`);
        }
      }
    } catch (err: any) {
      logError('Import error', err);
      let errorMessage = 'Import failed. Please try again.';
      
      if (err.response?.data) {
        const responseData = err.response.data;
        if (responseData.message) {
          errorMessage = String(responseData.message);
        } else if (responseData.error) {
          errorMessage = String(responseData.error);
        }
      } else if (err.message) {
        errorMessage = String(err.message);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setSuccess(null);
    setImportResult(null);
    setCurrentStep('upload');
    setPreviewData(null);
    setPreviewRows([]);
    setOptions({
      skip_duplicates: true,
      send_invites: false,
      validate_only: false,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Handle preview import - parse file and show preview
  const handlePreviewImport = async () => {
    if (!file) {
      setError('Please select a file to import');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await guestAPI.previewImport(eventId, file);
      
      if (result.success && result.data) {
        setPreviewData(result.data);
        setPreviewRows(result.data.rows);
        setCurrentStep('preview');
      } else {
        setError(result.error?.message || 'Failed to parse file');
      }
    } catch (err: any) {
      logError('Preview import error', err);
      setError(err.response?.data?.error?.message || err.message || 'Failed to preview import');
    } finally {
      setLoading(false);
    }
  };

  // Handle confirm import from preview
  const handleConfirmPreviewImport = async () => {
    if (!previewRows.length) {
      setError('No rows to import');
      return;
    }

    setLoading(true);
    setCurrentStep('importing');
    setError(null);

    try {
      const result = await guestAPI.confirmImport(eventId, previewRows, {
        skip_duplicates: options.skip_duplicates,
        send_invites: options.send_invites,
      });

      if (result.success) {
        const successful = result.data?.results?.successful || 0;
        const failed = result.data?.results?.failed || 0;
        
        if (successful > 0) {
          setSuccess(`Import completed! ${successful} guest(s) added successfully.${failed > 0 ? ` ${failed} failed.` : ''}`);
          onImportComplete();
          
          // Close modal after delay
          await delay(2000);
          handleClose();
        } else {
          setError('Import failed: No guests were added.');
          setCurrentStep('preview');
        }
      } else {
        setError(result.error?.message || 'Import failed');
        setCurrentStep('preview');
      }
    } catch (err: any) {
      logError('Confirm import error', err);
      setError(err.response?.data?.error?.message || err.message || 'Import failed');
      setCurrentStep('preview');
    } finally {
      setLoading(false);
    }
  };

  // Go back to upload step
  const handleBackToUpload = () => {
    setCurrentStep('upload');
    setPreviewData(null);
    setPreviewRows([]);
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownloadTemplate = () => {
    // Create Excel template: Full Name, First Name, Title, Last Name, Phone, Email,
    // then Companion 1-5 each with Name, Phone, Relationship, then Notes.
    // Smart parser (Full Name) extracts only one companion; use Companion 2-5 columns for more.
    const companionHeaders: string[] = [];
    for (let n = 1; n <= 5; n++) {
      companionHeaders.push(`Companion ${n} Name`, `Companion ${n} Phone`, `Companion ${n} Relationship`);
    }
    const headers = [
      'Full Name',
      'First Name',
      'Title',
      'Last Name',
      'Phone',
      'Email',
      ...companionHeaders,
      'Notes',
    ];

    // Sample rows: order matches headers (Full Name, First Name, Title, Last Name, Phone, Email, Comp1 Name, Comp1 Phone, Comp1 Rel, ... Notes)
    const emptyCompanions = Array(15).fill(''); // 5 companions × 3 fields
    const sampleRows = [
      // Smart parser: one companion from Full Name
      ['Mr. & Mrs. John Doe (Jane Doe)', '', '', '', '962771234567', 'john.doe@example.com', '', '', 'spouse', ...Array(12).fill(''), 'VIP couple'],
      // Multiple companions via columns (Companion 1–2 filled)
      ['', 'Maria', 'Ms.', 'Garcia', '971501234567', 'maria@example.com', 'Carlos Garcia', '971502345678', 'spouse', 'Anna Garcia', '971503456789', 'child', ...Array(9).fill(''), 'Vegetarian'],
      // Traditional: First + Last, one companion in columns
      ['', 'Robert', 'Mr.', 'Taylor', '962771112222', 'robert@example.com', 'Sarah Taylor', '962772223333', 'spouse', ...Array(12).fill(''), ''],
      // Full Name with & Guest (smart parser: one companion)
      ['Ms. Alice Williams & Guest', '', '', '', '966501234567', '', ...emptyCompanions, ''],
    ];

    const workbook = XLSX.utils.book_new();
    const worksheetData = [headers, ...sampleRows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    const companionWidths = [];
    for (let i = 0; i < 5; i++) {
      companionWidths.push({ wch: 18 }, { wch: 15 }, { wch: 12 }); // Name, Phone, Relationship
    }
    const columnWidths = [
      { wch: 40 }, // Full Name
      { wch: 15 }, // First Name
      { wch: 8 },  // Title
      { wch: 15 }, // Last Name
      { wch: 15 }, // Phone
      { wch: 25 }, // Email
      ...companionWidths,
      { wch: 20 }, // Notes
    ];
    worksheet['!cols'] = columnWidths;

    // Phone columns: index 4 (Phone), then 7, 10, 13, 16, 19 (Companion 1-5 Phone)
    const phoneColumnIndices = [4, 7, 10, 13, 16, 19];
    const phoneFormat = '0';
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      for (const phoneColumnIndex of phoneColumnIndices) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: phoneColumnIndex });
        if (worksheet[cellAddress] && worksheet[cellAddress].v) {
          worksheet[cellAddress].z = phoneFormat;
          worksheet[cellAddress].t = 'n';
        }
      }
    }
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Guest Import Template');
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Create and download Excel file
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `guest_import_template_${new Date().toISOString().split('T')[0]}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setSuccess('Template downloaded successfully!');
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!loading && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalScroll = (e: React.WheelEvent) => {
    e.stopPropagation();
  };

  const handleModalTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
      onClick={handleOverlayClick}
      onWheel={handleModalScroll}
      onTouchMove={handleModalTouchMove}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      <div 
        ref={modalRef}
        className={`relative w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overscroll-contain ${
          currentStep === 'preview' ? 'max-w-[95vw] xl:max-w-[1600px]' : 'max-w-3xl'
        }`}
        onWheel={handleModalScroll}
        onTouchMove={handleModalTouchMove}
      >
        <div 
          className={`rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}
          onClick={(e) => {
            // Prevent clicks inside modal from bubbling up to backdrop
            e.stopPropagation();
          }}
        >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex-1 min-w-0">
            <h2 className={`text-xl sm:text-2xl font-bold mb-1 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Import Guest List</h2>
            <p className={`text-xs sm:text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>Upload a CSV or Excel file to import multiple guests at once</p>
          </div>
          <div className="flex gap-2 shrink-0 w-full sm:w-auto">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowHelp(true)}
              className="flex items-center"
            >
              <HelpCircle className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Need Help?</span>
              <span className="sm:hidden">Help</span>
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleDownloadTemplate}
              className="flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Download Template</span>
              <span className="sm:hidden">Template</span>
            </Button>
          </div>
        </div>

        {/* Help Popup - Modern Design */}
        {showHelp && (
          <div className={`mb-6 rounded-2xl border overflow-hidden shadow-lg ${
            theme === 'dark' 
              ? 'bg-gray-800/95 border-gray-700 backdrop-blur-sm' 
              : 'bg-white border-gray-200'
          }`}>
            {/* Header */}
            <div className={`px-5 py-4 flex items-center justify-between border-b ${
              theme === 'dark' ? 'border-gray-700 bg-gradient-to-r from-blue-900/40 to-purple-900/40' : 'border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${
                  theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'
                }`}>
                  <FileSpreadsheet className={`h-5 w-5 ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                </div>
                <div>
                  <h3 className={`font-semibold text-base ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>Import Guide</h3>
                  <p className={`text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>Everything you need to know</p>
                </div>
              </div>
              <button
                onClick={() => setShowHelp(false)}
                className={`p-2 rounded-xl transition-colors ${
                  theme === 'dark' ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                }`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content Grid */}
            <div className="p-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                
                {/* Column 1: Supported Columns */}
                <div className={`rounded-xl p-4 ${
                  theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 rounded-lg ${
                      theme === 'dark' ? 'bg-emerald-500/20' : 'bg-emerald-100'
                    }`}>
                      <FileText className={`h-4 w-4 ${
                        theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
                      }`} />
                    </div>
                    <h4 className={`font-medium text-sm ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Supported Columns</h4>
                  </div>
                  
                  <div className="space-y-2">
                    {/* Name fields - either/or required */}
                    <div className={`py-1.5 px-2 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-800/50' : 'bg-white'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                          }`}>Full Name</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            theme === 'dark' ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600'
                          }`}>Required*</span>
                        </div>
                        <span className={`text-[11px] ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>Smart parser for complex names</span>
                      </div>
                    </div>
                    <div className={`flex items-center justify-center py-0.5 ${
                      theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                    }`}>
                      <span className="text-[10px] font-medium">— OR —</span>
                    </div>
                    <div className={`py-1.5 px-2 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-800/50' : 'bg-white'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                          }`}>First + Last Name</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            theme === 'dark' ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600'
                          }`}>Required*</span>
                        </div>
                        <span className={`text-[11px] ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>Standard name columns</span>
                      </div>
                    </div>
                    
                    {/* Divider */}
                    <div className={`border-t my-2 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}></div>
                    
                    {/* Other fields */}
                    {[
                      { name: 'Title', desc: 'After First Name — Mr., Mrs., Dr., H.E., etc.', required: false },
                      { name: 'Phone', desc: 'With country code (962...)', required: true },
                      { name: 'Email', desc: 'Guest email address', required: false },
                      { name: 'Companion 1–5', desc: 'Each has Name, Phone, Relationship columns (separate fields)', required: false },
                    ].map((col, i) => (
                      <div key={i} className={`flex items-center justify-between py-1.5 px-2 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-800/50' : 'bg-white'
                      }`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                          }`}>{col.name}</span>
                          {col.required && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium">Required</span>
                          )}
                        </div>
                        <span className={`text-[11px] ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>{col.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Column 2: Phone Format */}
                <div className={`rounded-xl p-4 ${
                  theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 rounded-lg ${
                      theme === 'dark' ? 'bg-blue-500/20' : 'bg-blue-100'
                    }`}>
                      <Phone className={`h-4 w-4 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                      }`} />
                    </div>
                    <h4 className={`font-medium text-sm ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Phone Format</h4>
                  </div>
                  
                  <p className={`text-xs mb-3 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>Include country code for all phone numbers</p>
                  
                  <div className="space-y-2">
                    {[
                      { code: '962', country: 'Jordan', flag: '🇯🇴', example: '962791234567' },
                      { code: '966', country: 'Saudi Arabia', flag: '🇸🇦', example: '966501234567' },
                      { code: '971', country: 'UAE', flag: '🇦🇪', example: '971501234567' },
                    ].map((item, i) => (
                      <div key={i} className={`flex items-center gap-3 py-2 px-3 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-800/50' : 'bg-white'
                      }`}>
                        <span className="text-lg">{item.flag}</span>
                        <div className="flex-1">
                          <span className={`text-xs font-medium ${
                            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                          }`}>{item.country}</span>
                        </div>
                        <code className={`text-xs px-2 py-1 rounded ${
                          theme === 'dark' ? 'bg-gray-700 text-emerald-400' : 'bg-gray-100 text-emerald-600'
                        }`}>{item.example}</code>
                      </div>
                    ))}
                    
                    {/* Invalid example */}
                    <div className={`flex items-center gap-3 py-2 px-3 rounded-lg border border-dashed ${
                      theme === 'dark' ? 'border-red-800/50 bg-red-900/10' : 'border-red-200 bg-red-50/50'
                    }`}>
                      <X className={`h-4 w-4 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`} />
                      <span className={`text-xs line-through ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                      }`}>0791234567</span>
                      <span className={`text-[11px] ${
                        theme === 'dark' ? 'text-red-400' : 'text-red-500'
                      }`}>Missing country code</span>
                    </div>
                  </div>
                </div>

                {/* Full Width: Smart Name Parsing */}
                <div className={`lg:col-span-2 rounded-xl p-4 ${
                  theme === 'dark' ? 'bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-indigo-800/50' : 'bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 rounded-lg ${
                      theme === 'dark' ? 'bg-indigo-500/20' : 'bg-indigo-100'
                    }`}>
                      <Sparkles className={`h-4 w-4 ${
                        theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'
                      }`} />
                    </div>
                    <h4 className={`font-medium text-sm ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Smart Name Parsing</h4>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      theme === 'dark' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-600'
                    }`}>One companion from Full Name</span>
                  </div>
                  <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Full Name parses only one companion (e.g. Mr. & Mrs. John (Jane)). Use Companion 2–5 columns for more.
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { input: 'Mr. & Mrs. John Smith (Jane)', primary: 'Mr. John Smith', companion: 'Mrs. Jane Smith' },
                      { input: 'Dr. James Wilson & Ms. Sarah Connor', primary: 'Dr. James Wilson', companion: 'Ms. Sarah Connor' },
                      { input: 'H.E. Mr. & Mrs. Robert Johnson', primary: 'H.E. Mr. Robert Johnson', companion: 'Mrs. Johnson' },
                      { input: 'Ms. Emily Davis & Guest', primary: 'Ms. Emily Davis', companion: 'Placeholder' },
                    ].map((example, i) => (
                      <div key={i} className={`p-3 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-900/50' : 'bg-white/80'
                      }`}>
                        <div className={`text-xs font-medium mb-2 ${
                          theme === 'dark' ? 'text-indigo-300' : 'text-indigo-600'
                        }`}>"{example.input}"</div>
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className={`px-2 py-0.5 rounded ${
                            theme === 'dark' ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                          }`}>{example.primary}</span>
                          <ArrowRight className={`h-3 w-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                          <span className={`px-2 py-0.5 rounded ${
                            example.companion === 'Placeholder' 
                              ? (theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')
                              : (theme === 'dark' ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700')
                          }`}>{example.companion}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Tips Banner */}
              <div className={`mt-4 flex items-center gap-3 p-3 rounded-xl ${
                theme === 'dark' ? 'bg-amber-900/20 border border-amber-800/50' : 'bg-amber-50 border border-amber-200'
              }`}>
                <Lightbulb className={`h-5 w-5 shrink-0 ${
                  theme === 'dark' ? 'text-amber-400' : 'text-amber-500'
                }`} />
                <p className={`text-xs ${
                  theme === 'dark' ? 'text-amber-200' : 'text-amber-700'
                }`}>
                  <strong>Pro tip:</strong> Download the template for a pre-formatted file with examples. Placeholder words like "& Guest" or "& Family" won't create separate companion accounts.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Success/Error Messages */}
        {success && (
          <Toast
            message={String(success)}
            type="success"
            onClose={() => setSuccess(null)}
          />
        )}
        {error && (
          <Toast
            message={String(error)}
            type="error"
            onClose={() => setError(null)}
          />
        )}

        {/* Preview Step */}
        {currentStep === 'preview' && previewData && (
          <div className="h-[60vh]">
            <GuestImportPreview
              eventId={eventId}
              rows={previewRows}
              onRowsChange={setPreviewRows}
              summary={previewData.summary}
              onConfirm={handleConfirmPreviewImport}
              onCancel={handleClose}
              onReupload={handleBackToUpload}
              loading={loading}
            />
          </div>
        )}

        {/* Upload Step - File Upload Area */}
        {currentStep === 'upload' && (
          <>
        <div className="mb-8">
          <label className={`block text-base sm:text-lg font-semibold mb-3 ${
            theme === 'dark' ? 'text-white' : 'text-gray-800'
          }`}>
            Select Guest List File
          </label>
          
          {!file ? (
            <div
              className={`border-2 border-dashed rounded-2xl p-6 sm:p-8 md:p-12 text-center transition-all duration-300 ${
                dragActive
                  ? (theme === 'dark'
                      ? 'border-blue-400 bg-gradient-to-br from-blue-900/30 to-indigo-900/30 scale-105 shadow-lg'
                      : 'border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 scale-105 shadow-lg')
                  : (theme === 'dark'
                      ? 'border-gray-600 hover:border-blue-400 hover:bg-gray-700/50 hover:shadow-md'
                      : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50 hover:shadow-md')
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-4 sm:mb-6 ${
                  theme === 'dark' 
                    ? 'bg-gradient-to-br from-blue-900/50 to-indigo-900/50' 
                    : 'bg-gradient-to-br from-blue-100 to-indigo-100'
                }`}>
                <svg
                    className={`w-6 h-6 sm:w-8 sm:h-8 ${
                      theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                </div>
                <p className={`text-base sm:text-xl font-semibold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Drop your guest list file here
                </p>
                <p className={`text-xs sm:text-sm mb-4 sm:mb-6 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Supports CSV and Excel files (.csv, .xlsx, .xls)
                </p>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => fileInputRef.current?.click()}
                  className=""
                >
                  Browse Files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileInput}
                  className="hidden"
                />
              </div>
              <div className={`mt-4 text-xs ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Supported formats: Excel (.xlsx, .xls) or CSV files (max 5MB)
              </div>
            </div>
          ) : (
            <div className={`border-2 rounded-2xl p-4 sm:p-6 shadow-lg ${
              theme === 'dark' 
                ? 'border-green-700 bg-gradient-to-r from-green-900/30 to-emerald-900/30' 
                : 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50'
            }`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="flex items-center min-w-0 flex-1">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mr-3 sm:mr-4 shrink-0 ${
                    theme === 'dark' ? 'bg-green-800/50' : 'bg-green-100'
                  }`}>
                  <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 ${
                        theme === 'dark' ? 'text-green-400' : 'text-green-600'
                      }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm sm:text-base md:text-lg font-semibold mb-1 truncate ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`} title={file.name}>{file.name}</div>
                    <div className={`text-xs sm:text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>{formatFileSize(file.size)} • Ready to import</div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleRemoveFile}
                  className="w-full sm:w-auto shrink-0"
                >
                  Remove File
                </Button>
              </div>
            </div>
          )}
        </div>


        {/* Detailed Validation Errors */}
        {importResult && !importResult.preview?.validation_passed && (
          <div className="mb-8">
            <h3 className={`text-lg font-semibold mb-4 flex items-center ${
              theme === 'dark' ? 'text-red-400' : 'text-red-700'
            }`}>
              <AlertTriangle className="h-5 w-5 mr-2" />
              Validation Errors Found
            </h3>
            <div className={`border rounded-2xl p-6 ${
              theme === 'dark' 
                ? 'bg-red-900/20 border-red-700/50' 
                : 'bg-red-50 border-red-200'
            }`}>
              {(() => {
                const validationErrors = importResult.preview?.validation_errors || importResult.validation_errors || [];
                if (validationErrors.length === 0) {
                  return (
                    <div className={theme === 'dark' ? 'text-red-400' : 'text-red-700'}>
                      <p className="font-medium">File validation failed</p>
                      <p className="text-sm mt-1">Please check your file format and try again.</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <p className={`font-medium text-sm sm:text-base ${
                        theme === 'dark' ? 'text-red-300' : 'text-red-800'
                      }`}>
                        Found {validationErrors.length} error{validationErrors.length > 1 ? 's' : ''} in your file
                      </p>
                      <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${
                        theme === 'dark' 
                          ? 'bg-red-800/50 text-red-300' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {validationErrors.length} issues
                </span>
                    </div>
                    
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {validationErrors.slice(0, 10).map((error, index) => (
                        <div key={index} className={`border rounded-lg p-3 ${
                          theme === 'dark' 
                            ? 'bg-gray-800 border-red-700/50' 
                            : 'bg-white border-red-200'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-1">
                                <span className={`text-xs px-2 py-1 rounded-full mr-2 ${
                                  theme === 'dark' 
                                    ? 'bg-red-800/50 text-red-300' 
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  Row {error.row_number}
                                </span>
                                <span className={`text-xs font-medium ${
                                  theme === 'dark' ? 'text-red-300' : 'text-red-800'
                                }`}>
                                  {error.error_type.replace('_', ' ').toUpperCase()}
                                </span>
                              </div>
                              <p className={`text-sm ${
                                theme === 'dark' ? 'text-red-400' : 'text-red-700'
                              }`}>{error.error_message}</p>
                              {error.row_data && (
                                <details className="mt-2">
                                  <summary className={`text-xs cursor-pointer ${
                                    theme === 'dark' 
                                      ? 'text-red-400 hover:text-red-300' 
                                      : 'text-red-600 hover:text-red-800'
                                  }`}>
                                    View row data
                                  </summary>
                                  <div className={`mt-1 text-xs p-2 rounded font-mono ${
                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                                  }`}>
                                    <pre>{JSON.stringify(error.row_data, null, 2)}</pre>
                                  </div>
                                </details>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {validationErrors.length > 10 && (
                        <div className="text-center py-2">
                          <span className="text-sm text-red-600">
                            ... and {validationErrors.length - 10} more errors
                </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="bg-red-100 border border-red-300 rounded-lg p-3">
                      <p className="text-sm text-red-800 font-medium mb-1">How to fix:</p>
                      <ul className="text-xs text-red-700 space-y-1">
                        <li>• Check that all required fields (First Name, Last Name, Phone) are filled</li>
                        <li>• Ensure phone numbers include country code (e.g., 962780808440 for Jordan, 966501234567 for Saudi Arabia)</li>
                        <li>• Verify email addresses are valid if provided</li>
                        <li>• Make sure names don't contain special characters</li>
                        <li>• Download the template to see the correct format</li>
                      </ul>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Import Results Preview */}
        {importResult?.preview && (
          <div className={`mb-6 p-6 rounded-2xl border-2 ${
            importResult.preview.validation_passed 
              ? 'bg-green-50 border-green-200' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 flex items-center ${
              importResult.preview.validation_passed ? 'text-green-800' : 'text-gray-900'
            }`}>
              {importResult.preview.validation_passed ? (
                <>
                  <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                  Validation Successful
                </>
              ) : (
                <>
                  <FileText className="h-5 w-5 mr-2" />
                  Import Preview
                </>
              )}
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center">
                <span className="text-gray-600">Total Rows:</span>
                <span className="ml-2 font-semibold text-gray-900">{importResult.preview.total_rows}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600">Validation:</span>
                <span className={`ml-2 font-semibold ${
                  importResult.preview.validation_passed ? 'text-green-600' : 'text-red-600'
                }`}>
                  {importResult.preview.validation_passed ? 'Passed' : 'Failed'}
                </span>
              </div>
            </div>
            
            {importResult.preview.validation_passed && (
              <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg">
                <p className="text-sm text-green-800 font-medium">
                  Your file is ready to import! All {importResult.preview.total_rows} rows passed validation.
                </p>
              </div>
            )}
            
            {importResult.preview.sample_data && importResult.preview.sample_data.length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Sample Data Preview ({Math.min(importResult.preview.sample_data.length, 3)} of {importResult.preview.sample_data.length} rows):
                </div>
                <div className="bg-white rounded-lg border p-3 text-xs font-mono max-h-64 overflow-y-auto space-y-2">
                  {importResult.preview.sample_data.slice(0, 3).map((row: any, index: number) => (
                    <div key={index} className="border-b border-gray-200 pb-2 last:border-b-0">
                      <div className="text-gray-500 mb-1">Row {index + 1}:</div>
                      <pre>{JSON.stringify(row, null, 2)}</pre>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Import Results */}
        {importResult?.data && (
          <div className="mb-6 p-4 bg-green-50 rounded-lg">
            <h3 className="text-sm font-medium text-green-800 mb-2">Import Results</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-700">Successful:</span>
                <span className="ml-2 font-medium">{importResult.data.results.successful}</span>
              </div>
              <div>
                <span className="text-green-700">Failed:</span>
                <span className="ml-2 font-medium">{importResult.data.results.failed}</span>
              </div>
              <div>
                <span className="text-green-700">Duplicates:</span>
                <span className="ml-2 font-medium">{importResult.data.results.duplicates}</span>
              </div>
              <div>
                <span className="text-green-700">Total Processed:</span>
                <span className="ml-2 font-medium">{importResult.data.results.total_processed}</span>
              </div>
            </div>
          </div>
        )}

        {/* Import Errors - Show when import fails but we have error details */}
        {importResult && !importResult.success && importResult.validation_errors && importResult.validation_errors.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center text-red-700">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Import Failed - Detailed Errors
            </h3>
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-red-800 font-medium">
                    Import failed with {importResult.validation_errors.length} error{importResult.validation_errors.length > 1 ? 's' : ''}
                  </p>
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                    {importResult.validation_errors.length} issues
                  </span>
                </div>
                
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {importResult.validation_errors.slice(0, 10).map((error, index) => (
                    <div key={index} className="bg-white border border-red-200 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-1">
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full mr-2">
                              Row {error.row_number}
                            </span>
                            <span className="text-xs font-medium text-red-800">
                              {error.error_type.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-red-700">{error.error_message}</p>
                          {error.row_data && (
                            <details className="mt-2">
                              <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800">
                                View row data
                              </summary>
                              <div className="mt-1 text-xs bg-gray-100 p-2 rounded font-mono">
                                <pre>{JSON.stringify(error.row_data, null, 2)}</pre>
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {importResult.validation_errors.length > 10 && (
                    <div className="text-center py-2">
                      <span className="text-sm text-red-600">
                        ... and {importResult.validation_errors.length - 10} more errors
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Action Buttons */}
        <div className={`flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-6 border-t ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            className="w-full sm:w-auto px-6 py-3 order-3 sm:order-1"
          >
            Cancel
          </Button>
          
          {file && (
            <Button
              type="button"
              variant="primary"
              onClick={handlePreviewImport}
              disabled={loading}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 order-1 sm:order-3"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                'Preview & Import'
              )}
            </Button>
          )}
        </div>
        </>
        )}
        </div>
      </div>
    </div>
  );

  // Use createPortal to render modal directly to document.body
  return createPortal(modalContent, document.body);
};
